<script setup>
import { isEmpty } from 'lodash'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AlertsApiHandler from '@/api/alerts'
import { ACTIONS_KEYS } from '@/utils/actions-keys'
import { ALERTS_LIMIT, FALLBACK_ALERT_DISPLAY_NAME } from '@/utils/check-ins/alerts-helpers'
import {
  BUTTON_ICON_DENSITIES,
  BUTTON_SIZES,
  BUTTON_SIZES_NEXT,
  BUTTON_VARIANTS
} from '@/utils/components-configurations/app-button'
import { DIALOG_DENSITY } from '@/utils/components-configurations/app-dialog'
import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { INFO_MESSAGE_TYPES } from '@/utils/components-configurations/app-info-message'
import { LIMIT_TAG_LIST_ENTITIES } from '@/utils/components-configurations/limit-tag-list'
import { ALERT_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { KEYWORD } from '@/utils/helpers'
import { objectOrNullProp } from '@/utils/prop-validators'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'

import AppDialog from '@/components/AppDialog'
import AlertModal from '@/components/check-ins/AlertModal'
import {
  CHECKINS_NOTIFICATIONS_CREATE_BUTTON_TEST_ID,
  CHECKINS_NOTIFICATIONS_LIMIT_MESSAGE_TEST_ID,
  CHECKINS_NOTIFICATIONS_TABLE_COLUMN_TEST_ID,
  CHECKINS_NOTIFICATIONS_TABLE_ROW_MENU_BUTTON_TEST_ID,
  CHECKINS_NOTIFICATIONS_TABLE_ROW_MENU_TEST_ID,
  CHECKINS_NOTIFICATIONS_TABLE_ROW_TEST_ID,
  CHECKINS_NOTIFICATIONS_TABLE_TEST_ID,
  CHECKINS_NOTIFICATIONS_TABLE_WRAPPER_TEST_ID
} from '@/components/check-ins/jest-helpers'
import LimitTagList from '@/components/form/LimitTagList'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppDeleteModal from '@/components/ui/AppDeleteModal/AppDeleteModal'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppInfoMessage from '@/components/ui/AppInfoMessage'
import AppRiveScene from '@/components/ui/AppRiveScene/AppRiveScene'
import AppTableCreateButton from '@/components/ui/AppTableCreateButton'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'
import SettingsPagesEmptyState from '@/components/ui/SettingsPagesEmptyState/SettingsPagesEmptyState'
import CheckinsAlertsTableLoader from '@/components/ui/SkeletonLoaders/CheckinAlertsTableLoader'
import AppTable from '@/components/ui/Table/AppTable'
import CheckinsAutomationsTemplate from '@/views/workspaces/settings/CheckinsAutomationsTemplate'

defineOptions({
  name: 'CheckInsAlerts'
})

const { t } = useI18n()

const isLimitExceeded = computed(() => alerts.value.length >= ALERTS_LIMIT)

const isEmptyAlerts = computed(() => {
  return !loaders.value.table && isEmpty(alerts.value) && isInitialDataLoaded.value
})

const isShowAlertModal = ref(false)

const createAlert = () => (isShowAlertModal.value = true)

const alertIdForEdit = ref(null)

const closeAlertModal = () => {
  isShowAlertModal.value = false
  alertIdForEdit.value = null
  scheduleReference.value = null
}

const editAlert = ({ id }) => {
  alertIdForEdit.value = id
  isShowAlertModal.value = true
}

const loaders = ref({
  table: false,
  deleteAlert: false
})

const isInitialDataLoaded = ref(false)

const alerts = ref([])

const tableHoverRow = ref(-1)

const { NAME, CHANNELS, ACTION } = TABLE_COLUMNS_KEYS

const TABLE_COLUMNS = [
  {
    title: t('alerts.checkins_from'),
    key: NAME,
    width: 'auto',
    sortable: true
  },

  {
    title: t('alerts.channel_name'),
    key: CHANNELS,
    width: 440
  },
  {
    key: ACTION,
    width: 24
  }
]

const fetchAlerts = async () => {
  try {
    loaders.value.table = true
    const api = new AlertsApiHandler()

    alerts.value = await api.fetchAlerts()
  } catch (error) {
    handleError({ error })
  } finally {
    loaders.value.table = false
  }
}

const scheduleReference = defineModel('createdSchedule', {
  default: null,
  validator: v => objectOrNullProp(v)
})

const confirmCreateAlertModalShow = ref(false)

const hideConfirmCreateAlertModal = () => {
  confirmCreateAlertModalShow.value = false
  scheduleReference.value = null
}

const confirmCreateAlert = () => {
  confirmCreateAlertModalShow.value = false
  createAlert()
}

watch(
  scheduleReference,
  newValue => {
    if (newValue) {
      confirmCreateAlertModalShow.value = true
    }
  },
  { immediate: true }
)

onMounted(async () => {
  await fetchAlerts()
  isInitialDataLoaded.value = true
})

const onReloadAlerts = async () => {
  await fetchAlerts()
}

const MENU_ITEM_ACTIONS = [
  {
    name: ACTIONS_KEYS.EDIT,
    title: 'action.edit',
    icon: 'edit-next'
  },
  {
    name: ACTIONS_KEYS.REMOVE,
    title: 'action.delete',
    icon: 'delete-next',
    color: 'var(--grade-low-color-next)'
  }
]

const isShowDeleteAlertModal = ref(false)
const alertForDelete = ref({})

const onDeleteAlertModalClose = () => {
  isShowDeleteAlertModal.value = false
  alertForDelete.value = {}
}

const onDeleteAlertConfirmed = async () => {
  loaders.value.deleteAlert = true
  const api = new AlertsApiHandler()

  try {
    const { id } = alertForDelete.value
    await api.deleteAlert({
      id
    })
    await onAlertDeleted()
  } catch (error) {
    handleError({ error })
  } finally {
    loaders.value.deleteAlert = false
  }
}

const onAlertDeleted = async () => {
  await onReloadAlerts()

  onDeleteAlertModalClose()
}

const onDeleteAlert = async alert => {
  alertForDelete.value = alert
  await nextTick()
  isShowDeleteAlertModal.value = true
}

const onMenuActionsClick = (name, item) => {
  if (name === ACTIONS_KEYS.EDIT) {
    editAlert(item)
  }
  if (name === ACTIONS_KEYS.REMOVE) {
    onDeleteAlert(item)
  }
}
</script>

<template>
  <CheckinsAutomationsTemplate
    :data-auto-testid="CHECKINS_NOTIFICATIONS_TABLE_WRAPPER_TEST_ID"
    :data-testid="CHECKINS_NOTIFICATIONS_TABLE_WRAPPER_TEST_ID"
    :has-items="!isEmptyAlerts"
    class="ca-Page"
    icon="message-channel"
  >
    <template #title>
      {{ t('checkins.alerts.title_message') }}
    </template>

    <template #subtitle>
      {{ t('checkins.alerts.subtitle_message') }}
    </template>

    <div class="ca-Page_Content">
      <AppTable
        v-if="!isEmptyAlerts"
        :columns="TABLE_COLUMNS"
        :data="alerts"
        :data-auto-testid="CHECKINS_NOTIFICATIONS_TABLE_TEST_ID"
        :data-testid="CHECKINS_NOTIFICATIONS_TABLE_TEST_ID"
        :disable-user-select="false"
        :hover-row="tableHoverRow"
        :loading="loaders.table"
        :row-test-id="CHECKINS_NOTIFICATIONS_TABLE_ROW_TEST_ID"
        border-on-last-row
        no-border
        offset-left="var(--cat-content-left-padding)"
        offset-right="var(--cat-content-right-padding)"
        type="primary-next"
      >
        <template #loading>
          <CheckinsAlertsTableLoader />
        </template>

        <template #cell="{ columnKey, item, index }">
          <template v-if="columnKey === NAME">
            <div
              :data-auto-testid="`${CHECKINS_NOTIFICATIONS_TABLE_COLUMN_TEST_ID}-${columnKey}`"
              :data-testid="`${CHECKINS_NOTIFICATIONS_TABLE_COLUMN_TEST_ID}-${columnKey}`"
              class="ca-CheckinsCell_Name ca-CheckinsCell_Link"
              @click="editAlert(item)"
            >
              <span
                v-tippy="{
                  content:
                    item[ALERT_ENTITY_KEYS.DISPLAY_NAME] || item[ALERT_ENTITY_KEYS.NAME] || null,
                  theme: DROP_LIST_THEMES.COMMON_TOOLTIP_THEMES,
                  placement: 'bottom-start',
                  maxWidth: '1164px'
                }"
                class="oboard-truncated-text"
              >
                {{
                  item[ALERT_ENTITY_KEYS.DISPLAY_NAME] ||
                  item[ALERT_ENTITY_KEYS.NAME] ||
                  FALLBACK_ALERT_DISPLAY_NAME
                }}
              </span>
            </div>
          </template>

          <div
            v-if="columnKey === CHANNELS"
            :data-auto-testid="`${CHECKINS_NOTIFICATIONS_TABLE_COLUMN_TEST_ID}-${columnKey}`"
            :data-testid="`${CHECKINS_NOTIFICATIONS_TABLE_COLUMN_TEST_ID}-${columnKey}`"
            class="ca-CheckinsCell_Channels"
          >
            <LimitTagList
              :entity="LIMIT_TAG_LIST_ENTITIES.CHANNEL"
              :items="item.channels || []"
              :limit="4"
              style="--list-gap: 8px; --count-offset: 0"
              tooltip-value="name"
            />
          </div>

          <template v-if="columnKey === ACTION">
            <DropdownMenu
              :data-auto-testid="CHECKINS_NOTIFICATIONS_TABLE_ROW_MENU_TEST_ID"
              :data-testid="CHECKINS_NOTIFICATIONS_TABLE_ROW_MENU_TEST_ID"
              :items="MENU_ITEM_ACTIONS"
              :offset="[0, 0]"
              position="bottom-end"
              type="default-next"
              @close="tableHoverRow = -1"
              @open="tableHoverRow = index"
              @item-click="onMenuActionsClick($event, item)"
            >
              <template #activator="{ isOpened }">
                <AppButton
                  :class="{ 'ca-ActionsButton-active': isOpened }"
                  :data-auto-testid="CHECKINS_NOTIFICATIONS_TABLE_ROW_MENU_BUTTON_TEST_ID"
                  :data-testid="CHECKINS_NOTIFICATIONS_TABLE_ROW_MENU_BUTTON_TEST_ID"
                  class="ca-ActionsButton"
                  icon="more-next"
                  size="sm"
                  type="tertiary-next"
                />
              </template>
            </DropdownMenu>
          </template>
        </template>

        <template #footer>
          <AppInfoMessage
            v-if="isLimitExceeded"
            :data-auto-testid="CHECKINS_NOTIFICATIONS_LIMIT_MESSAGE_TEST_ID"
            :data-testid="CHECKINS_NOTIFICATIONS_LIMIT_MESSAGE_TEST_ID"
            :type="INFO_MESSAGE_TYPES.WARNING"
            class="ca-InfoWarningText"
          >
            {{ t('checkins.alerts.reached_alerts_limit') }}
          </AppInfoMessage>

          <AppTableCreateButton
            v-else
            :data-auto-testid="CHECKINS_NOTIFICATIONS_CREATE_BUTTON_TEST_ID"
            :data-testid="CHECKINS_NOTIFICATIONS_CREATE_BUTTON_TEST_ID"
            class="ca-CreateAlertButton"
            full-width
            icon-name="plus-next"
            @click="createAlert"
          >
            {{ t('checkins.alerts.create') }}
          </AppTableCreateButton>
        </template>
      </AppTable>

      <SettingsPagesEmptyState v-if="isEmptyAlerts" class="ca-EmptyState" no-divider>
        <template #hero>
          <AppRiveScene
            autoplay
            file-src="https://static.oboard.io/no-checkin-notifcations-hero.riv"
            height="320"
            loop
            sync-with-modal-session
            width="480"
          >
            <template #fallback>
              <AppIcon class="ca-EmptyHero" height="315" icon-name="no-alerts-hero" width="441" />
            </template>
          </AppRiveScene>
        </template>
        <template #action>
          <AppButtonNext
            :data-auto-testid="CHECKINS_NOTIFICATIONS_CREATE_BUTTON_TEST_ID"
            :data-testid="CHECKINS_NOTIFICATIONS_CREATE_BUTTON_TEST_ID"
            :size="BUTTON_SIZES_NEXT.SM"
            :variant="BUTTON_VARIANTS.TERTIARY"
            icon-before="plus-next"
            @click="createAlert"
          >
            {{ t('checkins.alerts.create') }}
          </AppButtonNext>
        </template>
      </SettingsPagesEmptyState>
    </div>
  </CheckinsAutomationsTemplate>

  <portal to="modal-windows">
    <AlertModal
      :alert-id="alertIdForEdit"
      :opened="isShowAlertModal"
      :schedule-reference="scheduleReference"
      @on-close="closeAlertModal"
      @reload-alerts="onReloadAlerts"
    />

    <AppDeleteModal
      :keyword="KEYWORD"
      :loading="loaders.deleteAlert"
      :show="isShowDeleteAlertModal"
      :title="$t('checkins.alerts.delete.title')"
      keypath="checkins.alerts.delete.confirm_message"
      @on-close="onDeleteAlertModalClose"
      @on-confirm="onDeleteAlertConfirmed"
    />

    <AppDialog
      :density="DIALOG_DENSITY.COMPACT"
      :show="confirmCreateAlertModalShow"
      :size="435"
      banner-name="create-notification-from-reference-bg"
      class="ca-ConfirmCreateAlertModal"
      hide-header
      hide-hero
      @on-close="hideConfirmCreateAlertModal"
      @on-confirm="confirmCreateAlert"
    >
      <div class="ca-ConfirmCreateAlertModal_Banner">
        <AppButtonNext
          :icon-density="BUTTON_ICON_DENSITIES.FIT"
          :size="BUTTON_SIZES.XS"
          :variant="BUTTON_VARIANTS.LINK_GRAY"
          class="ca-CloseConfirmAlertModalButton"
          icon-before="close-next"
          style="--abn-background: transparent"
          @click="hideConfirmCreateAlertModal"
        />
      </div>

      <div class="ca-ConfirmCreateAlertModal_Content">
        <AppTitle :level="5">
          {{ t('alerts.create_from_reference.title') }}
        </AppTitle>

        <div class="ca-ConfirmCreateAlertModalDescription">
          {{ t('alerts.create_from_reference.description') }}
        </div>
      </div>

      <template #footer>
        <div class="ca-ConfirmCreateAlertModal_FooterActions">
          <AppButtonNext
            :variant="BUTTON_VARIANTS.SECONDARY_GRAY"
            @click="hideConfirmCreateAlertModal"
          >
            {{ t('alerts.create_from_reference.cancel') }}
          </AppButtonNext>
          <AppButtonNext @click="confirmCreateAlert">
            {{ t('alerts.create_from_reference.confirm') }}
          </AppButtonNext>
        </div>
      </template>
    </AppDialog>
  </portal>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/app-table-helpers';

.ca-Page_Content {
  width: 100%;
  max-width: $page-width-lg;
  gap: 24px;
}

.ca-CheckinsCell_Link {
  color: $primary-color-next;
  &:hover {
    text-decoration: underline;
  }
}

.ca-CheckinsCell_Name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: fw('semi-bold');
  cursor: pointer;
  padding-right: 8px;
}

.ca-CheckinsCell_Channels {
  padding-right: 8px;
}

.ca-InfoWarningText {
  margin-top: 10px;
  padding: 0 $page-right-padding 10px var(--cat-content-left-padding);
}

.ca-ActionsButton {
  &-active {
    background-color: $dark-2;
    color: $white;
  }
  @extend %app-table-hidden-items;
}

.ca-EmptyHero {
  margin-bottom: 49px;
}

.ca-ConfirmCreateAlertModal {
  --dialog-content-wrapper-padding: 0;
  --dialog-content-padding-right: 0;
  --dialog-content-padding-left: 0;
  --dialog-footer-border: none;

  :deep(.o-modal-content) {
    overflow: hidden;
  }
}

.ca-ConfirmCreateAlertModal_Banner {
  width: 435px;
  height: 240px;
  background-image: image-set(
    url('~@/assets/images/create-notification-from-reference-bg@1x.jpg') 1x,
    url('~@/assets/images/create-notification-from-reference-bg@2x.jpg') 2x,
    url('~@/assets/images/create-notification-from-reference-bg@3x.jpg') 3x,
    url('~@/assets/images/create-notification-from-reference-bg@4x.jpg') 4x
  );
  position: relative;
  padding: 20px;
}

.ca-CloseConfirmAlertModalButton {
  margin-left: auto;
}

.ca-ConfirmCreateAlertModal_Content {
  padding-inline: 20px;
  --title-level-5-margin: 20px 0;
}

.ca-ConfirmCreateAlertModalDescription {
  font-size: $fs-14;
  line-height: 20px;
  color: $dark-2;
}

.ca-ConfirmCreateAlertModal_FooterActions {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.ca-EmptyState {
  --spes-padding: 12px 0;
}

.ca-CreateAlertButton {
  border-bottom-left-radius: calc($border-radius-md - 1px);
  border-bottom-right-radius: calc($border-radius-md - 1px);
  --padding-left: var(--cat-content-left-padding);
}
</style>
